import { Flex, MenuProps } from 'antd';
import { useAppSelector, useUserPermissions } from '@Hooks/index';
import { getModalRecordKey } from '@Store/selectors/modalSelector';
import useResizeLayout from '@Hooks/useResizeLayout';
import classNames from 'classnames';
import { MongoAbility } from '@casl/ability';
import { ReactComponent as PlusAddIcon } from '@Icons/additional/plus-add.svg';
import { ReactComponent as ImportIcon } from '@Icons/additional/import.svg';
import { ReactComponent as ExportIcon } from '@Icons/additional/export.svg';
import { MoreOutlined } from '@ant-design/icons';
import { MButton, MDropdown } from '@Components/Base';
import styles from './PaymentButtonGroup.module.css';

interface PaymentButtonGroupProps {
  onAdd: () => void;
  onImport: (recordKey: string) => void;
  onExport: (recordKey: string) => void;
}

const items = ({
  onImport,
  onExport,
  permissions,
}: {
  onImport: () => void;
  onExport: () => void;
  permissions: MongoAbility;
}): MenuProps['items'] => {
  const importItem = permissions.can('Import', 'PurchaseRequest')
    ? {
        label: (
          <Flex gap={15} onClick={onImport}>
            <ImportIcon /> <span>Import PRs</span>
          </Flex>
        ),
        key: '0',
      }
    : null;

  const exportItem = permissions.can('Export', 'PurchaseRequest')
    ? {
        label: (
          <Flex gap={15} onClick={onExport}>
            <ExportIcon /> <span>Export Excel</span>
          </Flex>
        ),
        key: '0',
      }
    : null;

  const output: MenuProps['items'] = [importItem, exportItem];

  return output;
};

const PaymentButtonGroup: React.FC<PaymentButtonGroupProps> = ({
  onAdd,
  onImport,
  onExport,
}) => {
  const { isMobile } = useResizeLayout();
  const recordKey = useAppSelector(getModalRecordKey);
  const permissions = useUserPermissions();

  return (
    <Flex rootClassName={styles['button-group']} gap={isMobile ? 0 : 16}>
      {permissions.can('Create', 'TenderRequest') ? (
        <MButton
          className={classNames(styles['button-group__add-button'], {
            'trojan-button': !isMobile,
          })}
          onClick={onAdd}
          type={!isMobile ? 'primary' : undefined}
        >
          <PlusAddIcon /> {isMobile ? 'Tender' : 'Create tender'}
        </MButton>
      ) : null}
      {permissions.can('Import', 'PurchaseRequest') ||
      permissions.can('Export', 'PurchaseRequest') ? (
        <MButton
          className={classNames(styles['button-group__action-button'])}
          type="text"
        >
          <MDropdown
            menu={{
              items: items({
                onImport: () => onImport(recordKey),
                onExport: () => onExport(recordKey),
                permissions,
              }),
            }}
            trigger={['click']}
          >
            <div
              className={classNames(styles['button-group__actions'], {
                'trojan-neumorphism-box trojan-neumorphism-box-shadow':
                  !isMobile,
              })}
            >
              <MoreOutlined />
            </div>
          </MDropdown>
        </MButton>
      ) : null}
    </Flex>
  );
};

export default PaymentButtonGroup;
